import { useEffect, useRef, type KeyboardEvent } from 'react';
import { useChatSearch } from '../hooks/useChatSearch';
import { ArrowDown, ArrowUpIcon, CloseIcon, InputSearch, InputSearchHandle, SearchIcon } from '@repo/atomic-components';
import { HeaderIconButton } from './HeaderIconButton';

export interface ChatSearchBarProps {
  search: ReturnType<typeof useChatSearch>;
}

/** Search button for the chat header. The input bar (`ChatSearchInputBar`) renders below the header. */
export function ChatSearchBar({ search }: ChatSearchBarProps) {
  const { attachSearchButton, openSearch } = search;

  return (
    <HeaderIconButton
      ref={attachSearchButton}
      icon={<SearchIcon width={18} height={18} />}
      tooltip="Search chat by keyword"
      onClick={openSearch}
    />
  );
}

/** Search bar under the chat header: input, match counter, prev/next, close. */
export function ChatSearchInputBar({ search }: { search: ReturnType<typeof useChatSearch> }) {
  const {
    isSearchOpen,
    searchQuery,
    activeMatchIndex,
    totalMatches,
    closeSearch,
    dismissSearch,
    setSearchQuery,
    goToNextMatch,
    goToPrevMatch,
  } = search;
  const inputRef = useRef<InputSearchHandle | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSearchOpen) inputRef.current?.focus();
  }, [isSearchOpen]);

  // Clicking outside closes the search only when it's empty.
  useEffect(() => {
    if (!isSearchOpen) return;
    if (searchQuery.trim()) return;
    const el = barRef.current;
    if (!el) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (el.contains(target)) return;
      closeSearch();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isSearchOpen, searchQuery, closeSearch]);

  if (!isSearchOpen) return null;

  const hasQuery = Boolean(searchQuery.trim());
  const hasMatches = hasQuery && totalMatches > 0;
  // Prev/next wrap around, so they're only disabled when there are literally no matches.
  const canNavigate = hasMatches;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (event.shiftKey) goToPrevMatch();
    else goToNextMatch();
  };

  const handleBarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    dismissSearch();
  };

  return (
    <div
      ref={barRef}
      onKeyDown={handleBarKeyDown}
      className="flex w-full shrink-0 items-center overflow-hidden !px-4 py-2 [scrollbar-gutter:stable_both-edges] @[480px]:!px-12"
    >
      <div className="mx-auto flex w-full max-w-240 items-center gap-2">
        <div className="min-w-0 flex-1">
          <InputSearch
            ref={inputRef}
            type="text"
            placeholder="Search keyword"
            className="!bg-theme-white !text-theme-black !border-theme-border-grey !placeholder-theme-text-grey [&_button:hover]:!text-fletchers-gray-200 h-8 w-full [&_input]:text-sm [&_input]:placeholder:text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            clearIconClassName="text-theme-black"
          />
        </div>
        <span role="status" className="text-theme-black shrink-0 text-sm tabular-nums">
          {hasMatches && (
            <>
              {activeMatchIndex + 1} of {totalMatches}
              <span className="sr-only"> matches</span>
            </>
          )}
          {hasQuery && !hasMatches && 'No matches'}
        </span>
        <div className="flex items-center gap-0.5">
          {canNavigate && (
            <>
              <HeaderIconButton icon={<ArrowUpIcon />} tooltip="Previous match" onClick={goToPrevMatch} />
              <HeaderIconButton icon={<ArrowDown />} tooltip="Next match" onClick={goToNextMatch} />
            </>
          )}
          <HeaderIconButton
            icon={<CloseIcon width={12} height={12} />}
            tooltip="Close search"
            onClick={dismissSearch}
          />
        </div>
      </div>
    </div>
  );
}
